import React, { useEffect, useMemo, useState, useCallback } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import CrudPage, { ActiveBadge } from "@/components/CrudPage";
import KpiCard, { fmtNum } from "@/components/KpiCard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, Cell,
} from "recharts";

const MONTHS_TR = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
  "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
const tooltipStyle = { backgroundColor: "#111827", border: "1px solid #1F2937", borderRadius: 8, fontSize: 12 };
const TICK = { fontSize: 10, fill: "#9CA3AF" };
const LEGEND_STYLE = { fontSize: 11 };
const GROUPS = ["Makine", "Kompresör"];

export function monthLabel(m) {
  if (!m) return "-";
  const [y, mo] = m.split("-");
  return `${MONTHS_TR[+mo - 1]} ${y}`;
}

const currentMonth = () => new Date().toISOString().slice(0, 7);
const yearAgoMonth = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 11);
  return d.toISOString().slice(0, 7);
};

function MonthlyEntry({ models, onSaved }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [month, setMonth] = useState(currentMonth());
  const [qty, setQty] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/monthly-production?start_month=${month}&end_month=${month}`)
      .then((r) => {
        const map = {};
        r.data.forEach((x) => { map[x.model_code] = x.quantity; });
        setQty(map);
      }).catch(() => setQty({}));
  }, [month]);

  const totals = useMemo(() => {
    let mak = 0, kom = 0;
    models.forEach((m) => {
      const q = Number(qty[m.code]) || 0;
      if (m.group === "Kompresör") kom += q; else mak += q;
    });
    return { mak, kom, total: mak + kom };
  }, [qty, models]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/monthly-production/bulk", {
        month,
        entries: models.map((m) => ({ model_code: m.code, quantity: Number(qty[m.code]) || 0 })),
      });
      toast.success(`${monthLabel(month)} üretim adetleri kaydedildi`);
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="cp-entry-tab">
      <div className="flex flex-wrap items-center gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Ay *</Label>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
            className="w-44" data-testid="cp-entry-month" />
        </div>
        {isAdmin && (
          <Button onClick={save} disabled={saving} className="self-end" data-testid="cp-entry-save">
            {saving ? "Kaydediliyor..." : `${monthLabel(month)} Adetlerini Kaydet`}
          </Button>
        )}
        {!isAdmin && (
          <p className="text-xs text-muted-foreground self-end" data-testid="cp-entry-readonly">
            Veri girişi yalnızca yöneticiler tarafından yapılabilir.
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="cp-entry-totals">
        <Card className="p-4 bg-card border-border">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Toplam Makine</div>
          <div className="mt-1 text-2xl font-extrabold font-mono text-blue-400" data-testid="cp-total-makine">{fmtNum(totals.mak)} adet</div>
        </Card>
        <Card className="p-4 bg-card border-border">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Toplam Kompresör</div>
          <div className="mt-1 text-2xl font-extrabold font-mono text-amber-400" data-testid="cp-total-kompresor">{fmtNum(totals.kom)} adet</div>
        </Card>
        <Card className="p-4 bg-card border-primary/40">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Toplam Üretim</div>
          <div className="mt-1 text-2xl font-extrabold font-mono text-primary" data-testid="cp-total-all">{fmtNum(totals.total)} adet</div>
        </Card>
      </div>

      {GROUPS.map((g) => (
        <div key={g}>
          <h3 className="text-lg font-medium mb-2">{g === "Makine" ? "Makineler" : "Kompresörler"}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-5 gap-2.5">
            {models.filter((m) => m.group === g).map((m) => (
              <div key={m.code} className="flex items-center gap-2 p-2 rounded border border-border bg-secondary/30">
                <span className="text-xs font-medium flex-1 truncate" title={m.name}>{m.code}</span>
                <Input type="number" min="0" value={qty[m.code] ?? ""} disabled={!isAdmin}
                  onChange={(e) => setQty((q) => ({ ...q, [m.code]: e.target.value }))}
                  className="w-20 h-8 text-sm font-mono text-right" data-testid={`cp-qty-${m.code}`} placeholder="0" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function History({ models, refreshKey }) {
  const [start, setStart] = useState(yearAgoMonth());
  const [end, setEnd] = useState(currentMonth());
  const [data, setData] = useState({ monthly: [], per_model: [] });
  const [selModel, setSelModel] = useState("");

  useEffect(() => {
    api.get(`/analytics/monthly-production?start_month=${start}&end_month=${end}`)
      .then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [start, end, refreshKey]);

  const monthly = useMemo(() => {
    const byMonth = {};
    (data.monthly || []).forEach((m) => { byMonth[m.month] = m; });
    const out = [];
    let [y, mo] = start.split("-").map(Number);
    const [ey, em] = end.split("-").map(Number);
    while (y < ey || (y === ey && mo <= em)) {
      const k = `${y}-${String(mo).padStart(2, "0")}`;
      out.push(byMonth[k] || { month: k, makine: 0, kompresor: 0, total: 0 });
      mo += 1;
      if (mo > 12) { mo = 1; y += 1; }
    }
    return out;
  }, [data, start, end]);
  const latest = monthly[monthly.length - 1];
  const periodTotals = useMemo(() => monthly.reduce((a, m) => ({
    makine: a.makine + m.makine, kompresor: a.kompresor + m.kompresor, total: a.total + m.total,
  }), { makine: 0, kompresor: 0, total: 0 }), [monthly]);

  const selSeries = useMemo(() => {
    if (!selModel) return [];
    const pm = (data.per_model || []).find((x) => x.model_code === selModel);
    if (!pm) return [];
    return monthly.map((m) => ({ month: monthLabel(m.month), adet: pm.monthly[m.month] ?? 0 }));
  }, [selModel, data, monthly]);

  return (
    <div className="space-y-5" data-testid="cp-history-tab">
      <div className="flex flex-wrap items-center gap-2">
        <Input type="month" value={start} onChange={(e) => setStart(e.target.value)} className="w-40" data-testid="cp-history-start" />
        <span className="text-muted-foreground text-xs">—</span>
        <Input type="month" value={end} onChange={(e) => setEnd(e.target.value)} className="w-40" data-testid="cp-history-end" />
      </div>

      {latest && (
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-3" data-testid="cp-latest-cards">
          <Card className="p-4 bg-card border-primary/40">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{monthLabel(latest.month)}</div>
            <div className="mt-1 text-xl font-extrabold font-mono" data-testid="cp-latest-total">{fmtNum(latest.total)} adet</div>
            <div className="text-xs text-muted-foreground mt-0.5">Toplam üretim (son ay)</div>
          </Card>
          <KpiCard label="Dönem Toplam Makine" value={`${fmtNum(periodTotals.makine)} adet`} testid="cp-period-makine" />
          <KpiCard label="Dönem Toplam Kompresör" value={`${fmtNum(periodTotals.kompresor)} adet`} testid="cp-period-kompresor" />
          <KpiCard label="Dönem Toplam Üretim" value={`${fmtNum(periodTotals.total)} adet`} testid="cp-period-total" />
        </div>
      )}

      <Card className="p-4 bg-card border-border" data-testid="cp-trend-chart">
        <h3 className="text-lg font-medium mb-3">Aylık Üretim Trendi</h3>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={monthly.map((m) => ({ ...m, label: monthLabel(m.month) }))}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
            <XAxis dataKey="label" tick={TICK} />
            <YAxis tick={TICK} />
            <Tooltip contentStyle={tooltipStyle} />
            <Legend wrapperStyle={LEGEND_STYLE} />
            <Line dataKey="makine" name="Makine" stroke="#2563EB" strokeWidth={2.5} dot={false} />
            <Line dataKey="kompresor" name="Kompresör" stroke="#D97706" strokeWidth={2.5} dot={false} />
            <Line dataKey="total" name="Toplam" stroke="#DC2626" strokeWidth={2.5} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card className="p-4 bg-card border-border" data-testid="cp-compare-chart">
          <h3 className="text-lg font-medium mb-3">Makine / Kompresör Karşılaştırma</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={monthly.map((m) => ({ ...m, label: monthLabel(m.month) }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
              <XAxis dataKey="label" tick={TICK} />
              <YAxis tick={TICK} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={LEGEND_STYLE} />
              <Bar dataKey="makine" name="Makine" fill="#2563EB" radius={[3, 3, 0, 0]} />
              <Bar dataKey="kompresor" name="Kompresör" fill="#D97706" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-4 bg-card border-border" data-testid="cp-model-chart">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <h3 className="text-lg font-medium">Model Bazında Aylık Trend</h3>
            <Select value={selModel} onValueChange={setSelModel}>
              <SelectTrigger className="w-48 h-8 text-xs" data-testid="cp-model-select"><SelectValue placeholder="Model seçin" /></SelectTrigger>
              <SelectContent>
                {models.map((m) => (
                  <SelectItem key={m.code} value={m.code} data-testid={`cp-model-opt-${m.code}`}>{m.code} — {m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {selModel ? (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={selSeries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
                <XAxis dataKey="month" tick={TICK} />
                <YAxis tick={TICK} />
                <Tooltip contentStyle={tooltipStyle} />
                <Line dataKey="adet" name={selModel} stroke="#DC2626" strokeWidth={2.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-muted-foreground py-16 text-center">Trend için model seçin</p>
          )}
        </Card>
      </div>

      <Card className="p-4 bg-card border-border" data-testid="cp-model-bar-chart">
        <h3 className="text-lg font-medium mb-3">Model Bazında Toplam Üretim (dönem)</h3>
        <ResponsiveContainer width="100%" height={Math.max(260, (data.per_model?.length || 0) * 26)}>
          <BarChart data={data.per_model || []} layout="vertical" margin={{ left: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" horizontal={false} />
            <XAxis type="number" tick={TICK} />
            <YAxis type="category" dataKey="model_code" width={70} tick={TICK} />
            <Tooltip contentStyle={tooltipStyle} />
            <Bar dataKey="total" name="Toplam Adet" radius={[0, 3, 3, 0]}>
              {(data.per_model || []).map((p) => (
                <Cell key={p.model_code} fill={p.group === "Kompresör" ? "#D97706" : "#2563EB"} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <Card className="bg-card border-border overflow-hidden" data-testid="cp-monthly-table">
        <Table>
          <TableHeader>
            <TableRow className="border-border">
              {["Ay", "Makine", "Kompresör", "Toplam"].map((h) => (
                <TableHead key={h} className="text-xs font-semibold uppercase tracking-wider text-muted-foreground text-right first:text-left">{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {[...monthly].reverse().map((m) => (
              <TableRow key={m.month} className="border-border" data-testid={`cp-month-row-${m.month}`}>
                <TableCell className="text-sm py-2.5 font-medium">{monthLabel(m.month)}</TableCell>
                <TableCell className="text-sm py-2.5 text-right font-mono text-blue-400">{fmtNum(m.makine)}</TableCell>
                <TableCell className="text-sm py-2.5 text-right font-mono text-amber-400">{fmtNum(m.kompresor)}</TableCell>
                <TableCell className="text-sm py-2.5 text-right font-mono font-bold">{fmtNum(m.total)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

const MODEL_COLUMNS = [
  { key: "group", label: "Ürün Grubu", render: (m) => (
    <Badge className={m.group === "Kompresör"
      ? "bg-amber-600/20 text-amber-400 border-0 hover:bg-amber-600/20"
      : "bg-blue-600/20 text-blue-400 border-0 hover:bg-blue-600/20"}>{m.group}</Badge>
  ) },
  { key: "code", label: "Model Kodu" },
  { key: "name", label: "Model Adı" },
  { key: "active", label: "Durum", render: (m) => <ActiveBadge value={m.active} /> },
];

const MODEL_FIELDS = [
  { name: "code", label: "Model Kodu", required: true, placeholder: "Örn: PM-12000" },
  { name: "name", label: "Model Adı", required: true },
  { name: "group", label: "Ürün Grubu", type: "select", options: GROUPS.map((g) => ({ value: g, label: g })) },
  { name: "active", label: "Aktif", type: "checkbox" },
];

export default function CompletedProducts() {
  const [models, setModels] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = useCallback(() => {
    api.get("/models").then((r) => setModels(r.data.filter((m) => m.active !== false)))
      .catch((e) => toast.error(errMsg(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="completed-products-page" className="space-y-5">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Tamamlanan Ürünler</h1>
        <p className="text-sm text-muted-foreground mt-1">Makine ve kompresör modellerinin aylık üretim adetleri</p>
      </div>
      <Tabs defaultValue="entry">
        <TabsList className="bg-secondary" data-testid="cp-tabs">
          <TabsTrigger value="entry" data-testid="cp-tab-entry">Aylık Giriş</TabsTrigger>
          <TabsTrigger value="history" data-testid="cp-tab-history">Geçmiş & Trendler</TabsTrigger>
          <TabsTrigger value="models" data-testid="cp-tab-models">Model Listesi</TabsTrigger>
        </TabsList>
        <TabsContent value="entry" className="mt-4">
          <MonthlyEntry models={models} onSaved={() => setRefreshKey((k) => k + 1)} />
        </TabsContent>
        <TabsContent value="history" className="mt-4">
          <History models={models} refreshKey={refreshKey} />
        </TabsContent>
        <TabsContent value="models" className="mt-4">
          <CrudPage title="" subtitle="Önceden tanımlı makine ve kompresör modelleri"
            endpoint="models" testidPrefix="models"
            defaultForm={{ active: true, group: "Makine" }}
            columns={MODEL_COLUMNS} fields={MODEL_FIELDS} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
